import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

export function Cookies() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="pt-20 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-slate-900 mb-6">Cookie Policy</h1>
          <p className="text-slate-600 mb-8">Last updated: January 12, 2026</p>

          <div className="prose prose-lg max-w-none">
            <p className="text-slate-700 leading-relaxed mb-6">
              This Cookie Policy explains how dakkio.io, operated by MalexSoftware /
              RemoteSystems.ro ("we", "us", or "our"), uses cookies and similar technologies
              to recognize you when you visit our website at dakkio.io.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              1. What Are Cookies?
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              Cookies are small data files that are placed on your computer or mobile device
              when you visit a website. Cookies are widely used by website owners to make
              their websites work, work more efficiently, and provide reporting information.
            </p>
            <p className="text-slate-700 leading-relaxed mb-6">
              Cookies set by the website owner (in this case, dakkio.io) are called "first-party
              cookies." Cookies set by parties other than the website owner are called
              "third-party cookies." Third-party cookies enable third-party features or
              functionality to be provided on or through the website (e.g., analytics, advertising).
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              2. Why Do We Use Cookies?
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We use first-party and third-party cookies for several reasons:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>
                <strong>Essential Cookies:</strong> Required for the website to function
                properly
              </li>
              <li>
                <strong>Performance Cookies:</strong> Help us understand how visitors interact
                with our website
              </li>
              <li>
                <strong>Functionality Cookies:</strong> Remember your preferences and choices
              </li>
              <li>
                <strong>Targeting Cookies:</strong> Track your browsing habits to show
                relevant advertising
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              3. Types of Cookies We Use
            </h2>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              3.1 Essential Cookies
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              These cookies are strictly necessary to provide you with services available
              through our website and to use some of its features, such as access to secure
              areas. Because these cookies are strictly necessary, you cannot refuse them
              without impacting how our website functions.
            </p>
            <div className="bg-slate-50 rounded-lg p-6 mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 text-slate-900">Cookie Name</th>
                    <th className="text-left py-2 text-slate-900">Purpose</th>
                    <th className="text-left py-2 text-slate-900">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="py-2 text-slate-700">session_id</td>
                    <td className="py-2 text-slate-700">Maintains your logged-in state</td>
                    <td className="py-2 text-slate-700">Session</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-2 text-slate-700">csrf_token</td>
                    <td className="py-2 text-slate-700">Security protection</td>
                    <td className="py-2 text-slate-700">Session</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              3.2 Performance and Analytics Cookies
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              These cookies allow us to count visits and traffic sources so we can measure and
              improve the performance of our site. They help us understand which pages are the
              most and least popular and how visitors move around the site.
            </p>
            <div className="bg-slate-50 rounded-lg p-6 mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 text-slate-900">Cookie Name</th>
                    <th className="text-left py-2 text-slate-900">Purpose</th>
                    <th className="text-left py-2 text-slate-900">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="py-2 text-slate-700">_ga</td>
                    <td className="py-2 text-slate-700">Google Analytics tracking</td>
                    <td className="py-2 text-slate-700">2 years</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-2 text-slate-700">_gid</td>
                    <td className="py-2 text-slate-700">Google Analytics tracking</td>
                    <td className="py-2 text-slate-700">24 hours</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              3.3 Functionality Cookies
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              These cookies enable the website to provide enhanced functionality and
              personalization. They may be set by us or by third-party providers whose
              services we have added to our pages.
            </p>
            <div className="bg-slate-50 rounded-lg p-6 mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 text-slate-900">Cookie Name</th>
                    <th className="text-left py-2 text-slate-900">Purpose</th>
                    <th className="text-left py-2 text-slate-900">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="py-2 text-slate-700">preferences</td>
                    <td className="py-2 text-slate-700">Stores user preferences</td>
                    <td className="py-2 text-slate-700">1 year</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-2 text-slate-700">theme</td>
                    <td className="py-2 text-slate-700">Remembers theme choice</td>
                    <td className="py-2 text-slate-700">1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              3.4 Targeting/Advertising Cookies
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              These cookies may be set through our site by our advertising partners. They may
              be used by those companies to build a profile of your interests and show you
              relevant advertisements on other sites.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              4. How Can You Control Cookies?
            </h2>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              4.1 Browser Controls
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              Most web browsers allow you to control cookies through their settings. However,
              if you limit the ability of websites to set cookies, you may worsen your overall
              user experience, since it will no longer be personalized to you. Additionally,
              some features of our Service may not function properly.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              Here are links to cookie management in popular browsers:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>
                <a
                  href="https://support.google.com/chrome/answer/95647"
                  className="text-primary-600 hover:text-primary-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Chrome
                </a>
              </li>
              <li>
                <a
                  href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer"
                  className="text-primary-600 hover:text-primary-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Firefox
                </a>
              </li>
              <li>
                <a
                  href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac"
                  className="text-primary-600 hover:text-primary-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Safari
                </a>
              </li>
              <li>
                <a
                  href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                  className="text-primary-600 hover:text-primary-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Edge
                </a>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              4.2 Cookie Preferences
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              You can manage your cookie preferences through our cookie consent banner that
              appears when you first visit our website. You can change your preferences at
              any time by clicking the "Cookie Settings" link in the footer.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-3">
              4.3 Do Not Track
            </h3>
            <p className="text-slate-700 leading-relaxed mb-6">
              Some browsers incorporate a "Do Not Track" (DNT) feature that signals to websites
              that you do not want your online activity tracked. Currently, there is no uniform
              standard for recognizing and implementing DNT signals, so we do not respond to DNT
              signals.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              5. Third-Party Cookies
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              In addition to our own cookies, we may also use various third-party cookies to
              report usage statistics of the Service:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-700">
              <li>
                <strong>Google Analytics:</strong> We use Google Analytics to understand how
                visitors use our site. You can opt-out of Google Analytics by installing the
                Google Analytics opt-out browser add-on.
              </li>
              <li>
                <strong>Stripe:</strong> For payment processing (used on checkout pages only)
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              6. Updates to This Cookie Policy
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              We may update this Cookie Policy from time to time to reflect changes to the
              cookies we use or for other operational, legal, or regulatory reasons. Please
              revisit this Cookie Policy regularly to stay informed about our use of cookies.
            </p>
            <p className="text-slate-700 leading-relaxed mb-6">
              The date at the top of this Cookie Policy indicates when it was last updated.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-4">
              7. Contact Us
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              If you have any questions about our use of cookies, please contact us:
            </p>
            <ul className="list-none space-y-2 mb-6 text-slate-700">
              <li>
                <strong>Email:</strong>{' '}
                <a
                  href="mailto:privacy@dakkio.io"
                  className="text-primary-600 hover:text-primary-700"
                >
                  privacy@dakkio.io
                </a>
              </li>
              <li>
                <strong>Website:</strong>{' '}
                <a
                  href="https://remotesystems.ro"
                  className="text-primary-600 hover:text-primary-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  remotesystems.ro
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
